/**
 * Client ID generator (mirror of functions/lib/invoicePrefix.js).
 *
 * Format: `{prefix}-{FYYY}-{NNNN}` where prefix ∈ {AV1, RKH}.
 * FYYY = Indian FY 2-digit start + 2-digit end (FY 2026-27 → "2627").
 * NNNN = 4-digit zero-padded yearly sequence (per firm).
 *
 * The Cloud Function uses a Firestore transaction on `counters/client_{firm}_{fy}`
 * to allocate the next ID. Front-end mirrors that exact transaction for
 * manual-create flows so client IDs stay collision-safe across both paths.
 */

import {
  doc,
  runTransaction,
  serverTimestamp,
  type Firestore,
} from 'firebase/firestore';
import { db } from './firebase';
import type { FirmId, FirmPrefix } from '../types/client';
import { FIRM_PREFIX } from '../types/client';

export function getFyCode(date: Date = new Date()): string {
  if (isNaN(date.getTime())) {
    throw new Error('Invalid date for FY computation');
  }
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth(); // 0=Jan, 3=Apr
  const fyStart = month < 3 ? year - 1 : year;
  const fyEnd = fyStart + 1;
  const a = String(fyStart).slice(-2);
  const b = String(fyEnd).slice(-2);
  return `${a}${b}`;
}

function pad(n: number, width: number): string {
  const s = String(n);
  return s.length >= width ? s : '0'.repeat(width - s.length) + s;
}

function counterKey(firm: FirmId): 'AV' | 'RKH' {
  return firm === 'firm2' ? 'RKH' : 'AV';
}

export async function generateClientId(
  firm: FirmId,
  createdAt: Date = new Date(),
  firestore: Firestore = db
): Promise<string> {
  const prefix: FirmPrefix = FIRM_PREFIX[firm];
  const ck = counterKey(firm);
  const fy = getFyCode(createdAt);
  const counterId = `client_${ck}_${fy}`;
  const counterRef = doc(firestore, 'counters', counterId);

  const next = await runTransaction(firestore, async (tx) => {
    const snap = await tx.get(counterRef);
    const current = snap.exists() ? Number((snap.data() as { value?: number }).value) || 0 : 0;
    const nextVal = current + 1;
    tx.set(
      counterRef,
      {
        value: nextVal,
        firm: ck,
        fy,
        kind: 'client',
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    return nextVal;
  });

  const width = Math.max(4, String(next).length);
  return `${prefix}-${fy}-${pad(next, width)}`;
}

/** Compute the read-only invoice prefix shown on the create form. */
export function invoicePrefixPreview(firm: FirmId, createdAt: Date = new Date()): string {
  const fy = getFyCode(createdAt);
  return `${FIRM_PREFIX[firm]}-${fy}-NNNN`;
}
